// Mirrors the Go JSON. Kept hand-written rather than generated: the surface is
// small, and a wrong field name here is caught by the compiler at the call site.

export type User = { id: string; email: string; name: string | null; created_at: string }
export type Workspace = {
  id: string
  organization_id: string
  name: string
  slug: string
  video_model: string
}

export type Brand = {
  id: string
  workspace_id: string
  name: string
  /** The SPOKEN brand, which differs from the written one. "ContentStudio" is
   *  read as one token and has come back as "content start". */
  spoken_name: string
  url: string | null
  one_liner: string | null
  what_it_does: string | null
  key_benefits: string[] | null
  audience: string | null
  tone: string | null
  price_point: string | null
  avoid: string[] | null
  competitors: string[] | null
}

/** What the extractor returns. Written to the brand row as well, so the card
 *  usually reads it from there rather than from the task. */
export type BrandContext = {
  spoken_name: string
  one_liner: string
  what_it_does: string
  key_benefits: string[]
  audience: string
  tone: string
  price_point: string
  competitors: string[]
  avoid: string[]
}

export type DraftedScene = { line: string; gesture?: string; role: 'hook' | 'body' | 'cta' }
export type ScriptDraft = { hook_text: string; scenes: DraftedScene[]; notes: string }

export type Template = {
  id: string
  kind: 'hook' | 'bridge' | 'cta'
  name: string
  pattern: string
  category: string | null
  active: boolean
}

export type Avatar = {
  id: string
  workspace_id: string
  name: string
  gender: 'male' | 'female' | 'non_binary' | 'prefer_not_to_say' | null
  age_range: '18_24' | '25_34' | '35_44' | '45_54' | '55_plus' | null
  source_key: string | null
  closeup_key: string
  half_key: string
  voice_id: string
  generation_prompt: string | null
  created_at: string
}

export type Look = {
  id: string
  avatar_id: string
  name: string | null
  prompt: string | null
  start_frame_key: string
  mic: string
  space: string
  ambience: string | null
  camera_style: 'fixed' | 'handheld' | 'match_frame'
  speaking_to: 'camera' | 'off_camera' | 'match_frame'
  posture: 'seated' | 'standing' | 'match_frame'
  classification_status: 'pending' | 'classified' | 'failed' | null
  classification_attempted_at: string | null
  classification_failure_code: string | null
  camera_ownership: 'selfie' | 'operator' | 'fixed' | 'unclear' | null
  camera_ownership_confidence: number | null
  framing: 'close' | 'medium' | 'wide' | 'unclear' | null
  framing_confidence: number | null
  left_hand_state: 'free' | 'holding_object' | 'in_pocket' | 'out_of_frame' | 'unclear' | null
  left_hand_confidence: number | null
  right_hand_state: 'free' | 'holding_object' | 'in_pocket' | 'out_of_frame' | 'unclear' | null
  right_hand_confidence: number | null
  environment_type: 'home' | 'office' | 'cafe_restaurant' | 'street' | 'park_garden' | 'other_outdoor' | 'other_indoor' | 'unclear' | null
  environment_confidence: number | null
  occupancy: 'private' | 'quiet_public' | 'busy_public' | 'unclear' | null
  occupancy_confidence: number | null
  wind_exposure: 'sheltered' | 'exposed' | 'not_applicable' | 'unclear' | null
  wind_exposure_confidence: number | null
  visible_mic: 'lav' | 'handheld' | 'desk' | 'none' | 'unclear' | null
  visible_mic_confidence: number | null
  ambience_cached: boolean
  gestures: string[] | null
  saved: boolean
  created_at: string
}

export type Asset = {
  id: string
  brand_id: string
  kind: 'environment' | 'insert'
  name: string
  storage_key: string
  poster_key: string | null
  transcript: string | null
  duration_s: number | null
  created_at: string
}

export type JobStatus =
  | 'draft' | 'queued' | 'generating' | 'review' | 'approved' | 'failed' | 'aborted'

export type Job = {
  id: string
  workspace_id: string
  brand_id: string
  avatar_id: string
  look_id: string | null
  pain_point: string | null
  status: JobStatus
  stage: string | null
  cost_cents: number
  output_key: string | null
  video_model: string | null
  movement_mode: 'seated' | 'stationary' | 'walking' | 'match_frame'
  source_job_id: string | null
  regenerate_scenes: number[]
  creator_name: string | null
  poster_key: string | null
  error: string | null
  started_at: string | null
  finished_at: string | null
  cancel_requested: boolean
  created_at: string
}

export type Scene = {
  id: string
  job_id: string
  position: number
  kind: 'generated' | 'insert'
  line: string | null
  gesture: string | null
  hook_text: string | null
  asset_id: string | null
  chain_from: string | null
}

export type Take = {
  id: string
  scene_id: string
  take_number: number
  clip_key: string
  qc_status: 'pass' | 'fail' | 'unresolved' | null
  accepted: boolean
  cost_cents: number
}

export type JobDetail = { job: Job; scenes: Scene[]; takes: Record<string, Take[]> }

export type JobEvent = {
  id: string
  job_id: string
  stage: string | null
  level: 'info' | 'warn' | 'error'
  message: string
  created_at: string
}

export type UploadResult = {
  key: string
  size: number
  content_type: string
  filename: string
  poster_key?: string
  duration_s?: number
  has_audio?: boolean
  /** Transcription of a video starts at upload, so the form can show the text
   *  before the asset is saved. Absent when the file has no audio. */
  transcribe_task_id?: string
  silent?: boolean
  width?: number
  height?: number
}

export type Calibration = {
  acoustics: { mics: Record<string, string>; spaces: Record<string, string> }
  generation: { max_chain_depth: number; words_per_second_target: number; default_duration_s: number }
  cost: { credit_usd: number; absolute_cap_usd: number }
}

export type Role = 'viewer' | 'member' | 'admin' | 'owner'

const RANK: Record<Role, number> = { viewer: 1, member: 2, admin: 3, owner: 4 }

/** Mirrors the server's additive ladder. Used only to hide controls the caller
 *  cannot use -- the server enforces this independently. */
export const atLeast = (have: Role | undefined, want: Role) =>
  !!have && (RANK[have] ?? 0) >= RANK[want]

// --- brand films -------------------------------------------------------------
// Rendered by the separate brandfilm service; the API proxies it and keeps a
// workspace-scoped row per job.

export type BrandFilmStatus = 'queued' | 'running' | 'done' | 'qc_failed' | 'invalid' | 'failed'
export type BrandFilmAspect = '16:9' | '9:16' | '1:1'

export type BrandFilmStyle = {
  id: string
  label: string
  about: string
  aspects?: BrandFilmAspect[]
  lengthSeconds?: [number, number]
}

export type BrandFilmKit = { id: string; name: string; flows?: unknown[] }

export type BrandFilmConfig = {
  enabled: boolean
  message?: string
  styles: BrandFilmStyle[]
  kits: BrandFilmKit[]
}

export type BrandFilmConcept = {
  title: string
  style: string
  aspect: BrandFilmAspect
  seconds: number
  angle?: string
  hook?: string
  outline?: string[]
  brief: string
}

export type BrandFilmJob = {
  id: string
  workspace_id: string
  created_by: string | null
  creator_name: string | null
  service_job_id: string
  kind: 'film' | 'render'
  request: Record<string, unknown>
  title: string
  status: BrandFilmStatus
  created_at: string
  updated_at: string
}

export type BrandFilmQC = {
  pass: boolean
  errors: number
  warnings: number
  lufs: number | null
  true_peak: number | null
  duration: number | null
  review_score: number | null
}

export type BrandFilmRound = {
  round: number
  score: number | null
  audit_issues: number
  notes: unknown[]
}

/** The service's own job object, passed through verbatim. */
export type BrandFilmServiceJob = {
  id: string
  kind: 'film' | 'render'
  status: BrandFilmStatus
  log: string[]
  qc?: BrandFilmQC
  qc_errors?: string[]
  errors?: string[]
  rounds?: BrandFilmRound[]
  error?: string
  request?: Record<string, unknown>
}

export type BrandFilmDetail = {
  job: BrandFilmJob
  service: BrandFilmServiceJob | null
  service_error?: string
}
